import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ShoppingCartIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  aggregateProjectItemLists,
  formatNok,
} from "./projectItemListAggregates";
import { PMCustomSelector } from "../../../utils/PMCustomSelector";
import { itemListsApi } from "../../../../api/item_lists";
import { inventoryStoragesApi } from "../../../../api/inventory_storages";
import { axiosWithToast } from "../../../Utils";
import { useProjectPermissions } from "../../../../routes/roleOnly";

export const ItemListTab = ({ project }) => {
  const { t } = useTranslation();
  const projectPermissions = useProjectPermissions(project);
  const navigate = useNavigate();
  const [itemLists, setItemLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState(() => new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  useEffect(() => {
    fetchItemLists();
  }, [project.id]);

  const handleApprove = async (itemListId) => {
    await axiosWithToast(
      itemListsApi.approveItemList(itemListId, ""),
      () => fetchItemLists(),
      () => {},
      () => {},
      "Approving item list..."
    );
  };

  const handleReject = async (itemListId) => {
    const comment = prompt("Enter rejection reason (optional):");
    if (comment === null) return; // User cancelled

    await axiosWithToast(
      itemListsApi.rejectItemList(itemListId, comment || ""),
      () => fetchItemLists(),
      () => {},
      () => {},
      "Rejecting item list..."
    );
  };

  const handleDelete = async (itemListId) => {
    if (!confirm("Are you sure you want to delete this item list?")) return;

    await axiosWithToast(
      itemListsApi.deleteItemList(itemListId),
      () => fetchItemLists(),
      () => {},
      () => {},
      "Deleting item list..."
    );
  };

  const fetchItemLists = async () => {
    try {
      const response = await itemListsApi.getItemLists({ project_id: project?.id });
      if (response.data.success) {
        setItemLists(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching item lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "draft":
        return <PencilIcon className="h-4 w-4 text-gray-400" />;
      case "pending_approval":
        return <DocumentCheckIcon className="h-4 w-4 text-yellow-500" />;
      case "approved":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <TrashIcon className="h-4 w-4 text-red-500" />;
      case "invoiced_manually":
        return <CheckCircleIcon className="h-4 w-4 text-indigo-600" />;
      default:
        return <ClipboardDocumentListIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "invoiced_manually":
        return "bg-indigo-100 text-indigo-900";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeLabel = (type) => {
    return type === "consumption"
      ? t("project.item_list.type.consumption")
      : t("project.item_list.type.return");
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return t("project.item_list.status.draft");
      case "pending_approval":
        return t("project.item_list.status.pending_approval");
      case "approved":
        return t("project.item_list.status.approved");
      case "rejected":
        return t("project.item_list.status.rejected");
      case "invoiced_manually":
        return t("project.item_list.status.invoiced_manually");
      default:
        return status;
    }
  };

  const canSelectForMerge = (list) => list.status !== "invoiced_manually";

  const toggleListSelected = (id) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const eligibleForMerge = useMemo(
    () => itemLists.filter(canSelectForMerge),
    [itemLists]
  );

  const allEligibleSelected =
    eligibleForMerge.length > 0 &&
    eligibleForMerge.every((l) => selectedListIds.has(l.id));

  const projectListAggregates = useMemo(
    () => aggregateProjectItemLists(itemLists),
    [itemLists]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.item_list")}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {projectPermissions.canMergeItemListsToOrder &&
            eligibleForMerge.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => {
                  if (allEligibleSelected) setSelectedListIds(new Set());
                  else setSelectedListIds(new Set(eligibleForMerge.map((l) => l.id)));
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {allEligibleSelected
                  ? t("project.item_list.merge.deselect_all")
                  : t("project.item_list.merge.select_all_eligible")}
              </button>
              <button
                type="button"
                disabled={selectedListIds.size === 0}
                onClick={() => setMergeDialogOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ShoppingCartIcon className="h-4 w-4 mr-2" />
                {t("project.item_list.merge.create_order")}
                {selectedListIds.size > 0 ? ` (${selectedListIds.size})` : ""}
              </button>
            </>
          )}
          {projectPermissions.canCreateItemList ? (
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("project.item_list.create")}
          </button>
          ) : null}
        </div>
      </div>

      {/* Project item lists — summary */}
      <div className="bg-white shadow rounded-lg border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h4 className="text-sm font-semibold text-gray-900">
            {t("project.item_list.summary_title")}
          </h4>
          <Link
            to={`/dashboard/projects/${project.id}/item-lists/summary`}
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <ChartBarIcon className="h-4 w-4 mr-1.5" />
            {t("project.item_list.view_items_summary")}
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 sm:divide-x sm:divide-gray-200">
          <div className="px-4 py-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("project.item_list.summary_total_quantity")}
            </div>
            <div className="mt-0.5 text-lg font-semibold text-gray-900 tabular-nums">
              {projectListAggregates.totalQuantity}
            </div>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 sm:border-t-0">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("project.item_list.summary_total_purchase")}
            </div>
            <div className="mt-0.5 text-lg font-semibold text-gray-900 tabular-nums">
              {formatNok(projectListAggregates.totalPurchase)}
            </div>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 sm:border-t-0">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("project.item_list.summary_total_sales")}
            </div>
            <div className="mt-0.5 text-lg font-semibold text-gray-900 tabular-nums">
              {formatNok(projectListAggregates.totalSales)}
            </div>
          </div>
        </div>
      </div>

      {/* Item Lists Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {projectPermissions.canMergeItemListsToOrder &&
                  eligibleForMerge.length > 0 && (
                  <th className="pl-6 pr-2 py-3 w-10 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">{t("project.item_list.merge.select")}</span>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Navn
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("project.item_list.type")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("project.item_list.warehouse")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("itemListDetail.pick_date")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t("project.item_list.status")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Antall varer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opprettet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {itemLists.map((list) => (
                <tr key={list.id}>
                  {projectPermissions.canMergeItemListsToOrder &&
                    eligibleForMerge.length > 0 && (
                    <td className="pl-6 pr-2 py-4 whitespace-nowrap">
                      {canSelectForMerge(list) ? (
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedListIds.has(list.id)}
                          onChange={() => toggleListSelected(list.id)}
                          aria-label={t("project.item_list.merge.select_list", {
                            title: list.title,
                          })}
                        />
                      ) : (
                        <span className="inline-block w-4" />
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <Link
                        to={`/dashboard/projects/item-lists/${list.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-900"
                      >
                        {list.title}
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {list.list_type === "consumption"
                      ? t("project.item_list.type.consumption")
                      : list.list_type === "return"
                      ? t("project.item_list.type.return")
                      : list.list_type || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {list.storage?.title || list.storage?.storage_number || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {list.list_date || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        list.status
                      )}`}
                    >
                      {getStatusIcon(list.status)}
                      <span className="ml-1">
                        {getStatusLabel(list.status)}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {list.total_items}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>
                        {new Date(list.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs">av {list.created_by.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {list.can_edit && (
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {list.can_delete && (
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDelete(list.id)}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                      {list.can_approve &&
                        list.status === "pending_approval" && (
                          <button
                            className="text-green-600 hover:text-green-900"
                            onClick={() => handleApprove(list.id)}
                            title="Approve"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                      {list.can_approve &&
                        list.status === "pending_approval" && (
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleReject(list.id)}
                            title="Reject"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {itemLists.length === 0 && (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ingen varelister
            </h3>
            <p className="text-gray-500 mb-4">
              Opprett din første vareliste for dette prosjektet
            </p>
            {projectPermissions.canCreateItemList ? (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("project.item_list.create")}
            </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Create Item List Dialog */}
      {showCreateDialog && (
        <CreateItemListDialog
          project={project}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            fetchItemLists();
          }}
        />
      )}

      {mergeDialogOpen && (
        <MergeItemListsOrderDialog
          project={project}
          selectedIds={Array.from(selectedListIds)}
          onClose={() => setMergeDialogOpen(false)}
          onSuccess={(orderId) => {
            setMergeDialogOpen(false);
            setSelectedListIds(new Set());
            fetchItemLists();
            if (orderId) navigate(`/dashboard/orders/${orderId}`);
          }}
        />
      )}
    </div>
  );
};

const MergeItemListsOrderDialog = ({ project, selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("grouped_by_list");
  const [purchaseMarkup, setPurchaseMarkup] = useState("0");
  const [salesDiscount, setSalesDiscount] = useState("0");
  const [salesSurcharge, setSalesSurcharge] = useState("0");
  const [markInvoiced, setMarkInvoiced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedIds.length) return;
    setSubmitting(true);
    try {
      const res = await itemListsApi.mergeItemListsToOrder({
        project_id: project.id,
        item_list_ids: selectedIds,
        mode,
        purchase_markup_percent: parseFloat(purchaseMarkup) || 0,
        sales_discount_percent: parseFloat(salesDiscount) || 0,
        sales_surcharge_percent: parseFloat(salesSurcharge) || 0,
        mark_lists_invoiced: markInvoiced,
      });
      const data = res?.data;
      if (data?.success && data?.data?.order_id) {
        onSuccess(data.data.order_id);
      } else if (data?.errors?.length) {
        window.alert(data.errors.join("\n"));
      } else {
        window.alert(t("project.item_list.merge.error_generic"));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.join?.("\n") ||
        err?.message ||
        t("project.item_list.merge.error_generic");
      window.alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.item_list.merge.dialog_title")}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t("project.item_list.merge.dialog_hint", { count: selectedIds.length })}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("project.item_list.merge.mode_label")}
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="grouped_by_list">
                {t("project.item_list.merge.mode.grouped_by_list")}
              </option>
              <option value="one_line_per_list">
                {t("project.item_list.merge.mode.one_line_per_list")}
              </option>
              <option value="flat_by_product">
                {t("project.item_list.merge.mode.flat_by_product")}
              </option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("project.item_list.merge.purchase_markup")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={purchaseMarkup}
                onChange={(e) => setPurchaseMarkup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("project.item_list.merge.sales_discount")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={salesDiscount}
                onChange={(e) => setSalesDiscount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("project.item_list.merge.sales_surcharge")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={salesSurcharge}
                onChange={(e) => setSalesSurcharge(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={markInvoiced}
              onChange={(e) => setMarkInvoiced(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("project.item_list.merge.mark_invoiced_after")}
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              {t("project.item_list.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting
                ? t("project.item_list.merge.submitting")
                : t("project.item_list.merge.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Item List Dialog Component
const CreateItemListDialog = ({ project, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    type: "consumption",
    warehouse: null,
    listDate: new Date().toLocaleDateString("sv-SE"),
  });
  const [showWarehouseSelector, setShowWarehouseSelector] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.warehouse) {
      alert("Please select a warehouse");
      return;
    }

    setLoading(true);
    try {
      const response = await itemListsApi.createItemList({
        title: formData.name,
        list_type: formData.type,
        storage_id: formData.warehouse?.id,
        list_date: formData.listDate,
        module: 'project',
        module_param: project?.id,
        // include_in_calculation and should_invoice will be set by the system/processor later
      });

      if (response?.data?.success && response?.data?.data?.id) {
        // Close the dialog first
        onClose();
        
        // Navigate to the item list detail page
        navigate(`/dashboard/projects/item-lists/${response.data.data.id}`);
      }
    } catch (error) {
      console.error("Error creating item list:", error);
      alert("Kunne ikke opprette vareliste. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await inventoryStoragesApi.getInventoryStorages();
      console.log("fetchWarehouses response:", response);
      
      // Handle different response formats
      let warehouses = [];
      
      if (Array.isArray(response)) {
        warehouses = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          warehouses = response.data;
        } else if (response.data.success && Array.isArray(response.data.data)) {
          warehouses = response.data.data;
        } else if (Array.isArray(response.data.data)) {
          warehouses = response.data.data;
        }
      }
      
      // Ensure each warehouse has required fields and id
      warehouses = warehouses
        .filter(w => w && w.id)
        .map(w => ({
          ...w,
          display_name: w.display_name || w.title || w.storage_number || `Lager ${w.id}`,
        }));
      
      console.log("fetchWarehouses returning warehouses:", warehouses);
      return warehouses;
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      return [];
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t("project.item_list.create")}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Navn
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("project.item_list.type")}
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="consumption">
                  {t("project.item_list.type.consumption")}
                </option>
                <option value="return">
                  {t("project.item_list.type.return")}
                </option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("project.item_list.warehouse")}
              </label>
              <button
                type="button"
                onClick={() => setShowWarehouseSelector(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left bg-white"
              >
                {formData.warehouse
                  ? formData.warehouse.display_name
                  : t("project.item_list.select_warehouse")}
              </button>
            </div>

            <div>
              <label htmlFor="project-item-list-date" className="block text-sm font-medium text-gray-700 mb-1">
                {t("itemListDetail.pick_date")}
              </label>
              <input id="project-item-list-date" type="date" required
                value={formData.listDate}
                onChange={(e) => setFormData({ ...formData, listDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? t("common.creating") : t("common.create")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Warehouse Selector */}
      <PMCustomSelector
        open={showWarehouseSelector}
        onClose={() => setShowWarehouseSelector(false)}
        onSelect={(warehouse) => {
          setFormData({ ...formData, warehouse });
          setShowWarehouseSelector(false);
        }}
        title={t("project.item_list.select_warehouse")}
        description={t("project.item_list.select_warehouse_description")}
        fetchFunction={fetchWarehouses}
        field="display_name"
        field2="storage_number"
        field3="description"
        size="md"
      />
    </div>
  );
};
